/**
 * S02 · New Session — bottom-sheet route. Port of the showcase sheet with
 * real form state over the real template catalogue.
 *
 * The location row records where the session starts, which it did not do for
 * a long time: `Session.startLocation` existed in the shared schema with no
 * column behind it on either side, so the value was parsed and dropped twice
 * over. Client migration 7 and server revision 0015 gave it somewhere to live.
 *
 * The fix is requested on mount and never gates the button. Location is a
 * detail about the session; the session is the thing the inspector came for,
 * and denying permission or standing indoors must not stop them starting work.
 *
 * Validation is still a trimmed-title check rather than Zod. Every field this
 * sheet writes is constrained by `Session.parse` on the way into SQLite, so a
 * second gate here would duplicate rather than catch.
 */
import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Btn, Chip, I, Ico, T } from '@/components/primitives';
import { useActiveProject, useCreateSessionMutation, useProjects } from '@kapture/client-core';
import { DEFAULT_TEMPLATE_ID, listTemplates } from '@kapture/domain';
import { useGeolocation } from '@/lib/device/useGeolocation';

export const Route = createFileRoute('/sessions/new')({
  component: NewSessionSheet,
  staticData: { mobile: true, modal: true },
});

function NewSessionSheet() {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [templateId, setTemplateId] = useState<string | null>(DEFAULT_TEMPLATE_ID);
  const templates = listTemplates();
  const createSession = useCreateSessionMutation();
  const geo = useGeolocation();

  const { data: activeProject } = useActiveProject();
  const { data: projects = [] } = useProjects();
  // Seeded from the active project, then owned by this sheet: the inspector
  // can file this one session somewhere else without changing what they are
  // working on for the rest of the day.
  const [projectId, setProjectId] = useState<string | null>(null);
  const [pickingProject, setPickingProject] = useState(false);
  // Whether the inspector has touched the form themselves. Once they have,
  // the project stops choosing for them — a default that overwrites a
  // deliberate choice is not a default.
  const [templateTouched, setTemplateTouched] = useState(false);
  const [seeded, setSeeded] = useState(false);

  const project = projects.find((p) => p.id === projectId) ?? null;

  // The carry-over. Runs once, when the active project first resolves: the
  // project comes across, and its default form with it unless the inspector
  // has already picked one.
  useEffect(() => {
    if (seeded || activeProject === undefined) return;
    setSeeded(true);
    if (!activeProject) return;
    setProjectId(activeProject.id);
    if (!templateTouched && activeProject.defaultTemplateId) {
      setTemplateId(activeProject.defaultTemplateId);
    }
  }, [activeProject, seeded, templateTouched]);

  function chooseProject(next: string | null) {
    setProjectId(next);
    setPickingProject(false);
    // Moving the session to another project brings that project's form with
    // it, on the same terms as the first carry-over.
    const target = projects.find((p) => p.id === next);
    if (!templateTouched && target?.defaultTemplateId) setTemplateId(target.defaultTemplateId);
  }

  function chooseTemplate(next: string | null) {
    setTemplateTouched(true);
    setTemplateId(next);
  }

  // Ask on open rather than on submit. A fix can take several seconds, and the
  // inspector spends those seconds typing a title — asking at submit would
  // either block the button or lose the location to a race.
  useEffect(() => {
    void geo.request();
    // Once, on mount. `geo.request` is stable, but re-running on every render
    // would re-prompt in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function close() {
    navigate({ to: '/' });
  }

  async function submit() {
    const clean = title.trim();
    if (!clean) return;
    // Whatever the fix did, the session gets created. A location is worth
    // having and never worth blocking on.
    const session = await createSession.mutateAsync({
      title: clean,
      templateId,
      projectId,
      startLocation: geo.point,
    });
    navigate({ to: '/sessions/$sessionId', params: { sessionId: session.id } });
  }

  const canSubmit = title.trim().length > 0 && !createSession.isPending;
  const activeTemplate = templates.find((t) => t.id === templateId) ?? null;

  // Each branch says what actually happened, because "no location" has four
  // different causes and only two of them are worth retrying.
  const locationLabel = geo.point
    ? `${geo.point.lat.toFixed(5)}, ${geo.point.lng.toFixed(5)}`
    : geo.status === 'requesting'
      ? 'Finding your location…'
      : geo.status === 'denied'
        ? 'Location permission denied — tap to ask again'
        : geo.status === 'unsupported'
          ? 'This browser has no location API'
          : geo.status === 'error'
            ? (geo.error ?? 'Location unavailable — tap to retry')
            : 'Tap to record where this starts';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Start new session"
      onClick={close}
      style={{
        position: 'absolute',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        fontFamily: T.font,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: T.surface,
          borderRadius: '20px 20px 0 0',
          padding: '12px 20px 28px',
          boxShadow: '0 -20px 40px rgba(0,0,0,0.15)',
          color: T.text,
        }}
      >
        <div
          style={{
            width: 36,
            height: 4,
            borderRadius: 2,
            background: T.stroke2,
            margin: '0 auto 16px',
          }}
        />
        <div
          style={{
            fontSize: 20,
            fontWeight: 700,
            fontFamily: T.font,
            letterSpacing: '-0.02em',
            marginBottom: 4,
          }}
        >
          Start new session
        </div>
        <div style={{ fontSize: 13, color: T.textDim, fontFamily: T.font, marginBottom: 16 }}>
          Pick a template or start blank
        </div>

        <div style={{ marginBottom: 12 }}>
          <label
            htmlFor="session-title"
            style={{
              fontSize: 11,
              color: T.textMute,
              fontFamily: T.font,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              marginBottom: 6,
              display: 'block',
            }}
          >
            Session name
          </label>
          <input
            id="session-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canSubmit) void submit();
            }}
            placeholder="e.g. Westbourne Bridge — Pier 3"
            autoFocus
            style={{
              width: '100%',
              padding: '12px 14px',
              border: `1.5px solid ${title.trim() ? T.ink : T.stroke}`,
              borderRadius: 10,
              fontSize: 15,
              fontFamily: T.font,
              fontWeight: 500,
              outline: 'none',
              color: T.text,
              background: T.surface,
            }}
          />
        </div>

        {/* Filed under — what the project already decided, shown rather than
            asked. Both rows are changeable here and neither is required: a
            session with no project and no form is a valid thing to start. */}
        <div style={{ marginBottom: 12 }}>
          <div
            style={{
              fontSize: 11,
              color: T.textMute,
              fontFamily: T.font,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              marginBottom: 6,
            }}
          >
            Filed under
          </div>

          <button
            type="button"
            onClick={() => setPickingProject((open) => !open)}
            aria-expanded={pickingProject}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: 12,
              minHeight: 66,
              width: '100%',
              borderRadius: T.r3,
              border: `1px solid ${T.stroke}`,
              background: T.surface,
              textAlign: 'left',
              font: 'inherit',
              color: 'inherit',
              cursor: 'pointer',
            }}
          >
            <span
              aria-hidden
              style={{
                width: 38,
                height: 38,
                borderRadius: 12,
                background: project ? T.brandSoft : T.surface2,
                color: project ? T.brandText : T.textDim,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Ico d={I.folder} size={18} />
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span
                style={{
                  display: 'block',
                  fontFamily: T.mono,
                  fontSize: 9.5,
                  fontWeight: 500,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: T.textMute,
                }}
              >
                {project ? 'Project · carried over' : 'Project'}
              </span>
              <span
                style={{
                  display: 'block',
                  fontSize: 15,
                  fontWeight: 700,
                  letterSpacing: '-0.015em',
                  marginTop: 3,
                  color: project ? T.text : T.textDim,
                }}
              >
                {project ? project.name : 'No project'}
              </span>
              {project && (
                <span
                  style={{
                    display: 'block',
                    fontFamily: T.mono,
                    fontSize: 10.5,
                    color: T.textDim,
                    marginTop: 2,
                  }}
                >
                  {project.code}
                </span>
              )}
            </span>
            <span style={{ color: T.brandText, fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
              Change
            </span>
          </button>

          {pickingProject && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
              {projects.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => chooseProject(p.id)}
                  style={{
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: `1px solid ${p.id === projectId ? T.brand : T.stroke}`,
                    background: p.id === projectId ? T.brandSoft : T.surface,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    textAlign: 'left',
                    font: 'inherit',
                    color: 'inherit',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ flex: 1 }}>
                    <span style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>{p.name}</span>
                    <span style={{ display: 'block', fontSize: 11, fontFamily: T.mono, color: T.textMute }}>
                      {p.code}
                      {p.defaultTemplateName ? ` · ${p.defaultTemplateName}` : ' · no default form'}
                    </span>
                  </span>
                  {p.id === projectId && <Ico d={I.check} size={14} stroke={T.brand} />}
                </button>
              ))}
              <button
                type="button"
                onClick={() => chooseProject(null)}
                style={{
                  padding: '10px 12px',
                  borderRadius: 10,
                  border: `1px solid ${projectId === null ? T.brand : T.stroke}`,
                  background: projectId === null ? T.brandSoft : T.surface,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  textAlign: 'left',
                  font: 'inherit',
                  color: 'inherit',
                  cursor: 'pointer',
                }}
              >
                <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>No project</span>
                {projectId === null && <Ico d={I.check} size={14} stroke={T.brand} />}
              </button>
            </div>
          )}

          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
              marginTop: 10,
              padding: '0 2px',
            }}
          >
            <Ico d={I.info} size={14} stroke={T.textMute} />
            <span style={{ fontSize: 12, color: T.textDim, lineHeight: 1.5 }}>
              {project
                ? 'Filled in from your project. Change either one here, or work with no project at all — nothing is required to start capturing.'
                : 'No project set. Capture works either way — a manager can file this later.'}
            </span>
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <div
            style={{
              fontSize: 11,
              color: T.textMute,
              fontFamily: T.font,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              marginBottom: 6,
            }}
          >
            {project?.defaultTemplateId && !templateTouched
              ? 'Form · default for this project'
              : 'Template'}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <button
              type="button"
              onClick={() => chooseTemplate(null)}
              style={{
                padding: '10px 12px',
                border: `1px solid ${templateId === null ? T.brand : T.stroke}`,
                background: templateId === null ? T.brandSoft : T.surface,
                color: templateId === null ? T.brandInk : T.text,
                borderRadius: 10,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                fontFamily: T.font,
                cursor: 'pointer',
                textAlign: 'left',
                boxShadow: templateId === null ? '0 0 0 3px rgba(255,90,31,0.08)' : 'none',
              }}
            >
              <div
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 7,
                  background: templateId === null ? T.brand : T.surface2,
                  color: templateId === null ? '#fff' : T.textDim,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Ico d={I.file} size={14} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>Blank session</div>
                <div style={{ fontSize: 11, color: T.textMute }}>
                  Capture-only, no structured form
                </div>
              </div>
            </button>
            {templates.map((t) => {
              const active = templateId === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => chooseTemplate(t.id)}
                  style={{
                    padding: '10px 12px',
                    border: `1px solid ${active ? T.brand : T.stroke}`,
                    background: active ? T.brandSoft : T.surface,
                    color: active ? T.brandInk : T.text,
                    borderRadius: 10,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    fontFamily: T.font,
                    cursor: 'pointer',
                    textAlign: 'left',
                    boxShadow: active ? '0 0 0 3px rgba(255,90,31,0.08)' : 'none',
                  }}
                >
                  <div
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: 7,
                      background: active ? T.brand : T.surface2,
                      color: active ? '#fff' : T.textDim,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Ico d={I.file} size={14} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{t.name}</div>
                    <div style={{ fontSize: 11, color: T.textMute, fontFamily: T.mono }}>
                      v{t.version} · {t.fields.length} fields · {t.sections.length} sections
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          {activeTemplate && (
            <div style={{ marginTop: 6, fontSize: 11, color: T.textMute, fontFamily: T.font }}>
              Form fields will appear under the session's Form tab.
            </div>
          )}
        </div>

        <div style={{ marginBottom: 18 }}>
          <div
            style={{
              fontSize: 11,
              color: T.textMute,
              fontFamily: T.font,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              marginBottom: 6,
            }}
          >
            Location
          </div>
          <button
            type="button"
            onClick={() => void geo.request()}
            aria-label={geo.point ? 'Refresh start location' : 'Get start location'}
            style={{
              width: '100%',
              padding: '10px 14px',
              border: `1px solid ${T.stroke}`,
              borderRadius: 10,
              background: 'transparent',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              textAlign: 'left',
              cursor: 'pointer',
              fontFamily: T.font,
            }}
          >
            <Ico d={I.pin} size={16} stroke={geo.point ? T.brand : T.textDim} />
            <div style={{ flex: 1, fontSize: 13, fontFamily: T.font, color: T.textDim }}>
              {locationLabel}
            </div>
            <Chip tone={geo.point ? 'brandSoft' : 'neutral'} size="sm">
              {geo.status === 'requesting' ? '…' : geo.point ? 'Set' : 'Tap'}
            </Chip>
          </button>
        </div>

        {createSession.isError && (
          <div
            style={{
              marginBottom: 10,
              padding: '8px 12px',
              background: T.dangerSoft,
              border: `1px solid ${T.dangerSoft}`,
              borderRadius: 10,
              color: T.dangerText,
              fontSize: 12,
              fontFamily: T.font,
            }}
          >
            Couldn't save session. Try again.
          </div>
        )}

        <Btn
          tone="primary"
          size="lg"
          full
          disabled={!canSubmit}
          onClick={() => void submit()}
          iconRight={<Ico d={I.arrow} size={18} />}
          style={{
            opacity: canSubmit ? 1 : 0.5,
            cursor: canSubmit ? 'pointer' : 'not-allowed',
          }}
        >
          {createSession.isPending ? 'Saving…' : 'Start capturing'}
        </Btn>
        <div style={{ height: 6 }} />
        <Btn tone="ghost" size="md" full onClick={close}>
          Cancel
        </Btn>
      </div>
    </div>
  );
}
